import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/components/BrandLogo";
import DayPhaseTimeline from "@/components/DayPhaseTimeline";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/dashboard", label: "Dashboard" },
  { to: "/grafici", label: "Grafici" },
  { to: "/storico", label: "Storico" },
  { to: "/previsioni", label: "Previsioni" },
  { to: "/stazione", label: "La Stazione" },
  { to: "/contatti", label: "Contatti" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-light shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 lg:h-20 flex items-center justify-between gap-6">
        {/* Left: brand */}
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group flex-shrink-0">
          <BrandLogo />
        </Link>

        {/* Center: day phase timeline */}
        <div className="hidden lg:flex flex-1 justify-center">
          <DayPhaseTimeline />
        </div>

        {/* Right: nav + CTA */}
        <div className="hidden lg:flex items-center gap-1 flex-shrink-0">
          {LINKS.slice(1, 6).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-[13px] font-medium transition-colors ${
                  isActive ? "bg-[#0B132B] text-white" : "text-slate-700 hover:text-[#0B132B] hover:bg-slate-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/contatti" className="ml-1">
            <Button data-testid="nav-cta-live" size="sm" className="rounded-full bg-[#0EA5E9] hover:bg-[#0284c7] px-4 h-9 text-[13px]">
              Contatti
            </Button>
          </Link>
        </div>

        {/* Mobile */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button data-testid="mobile-menu-btn" variant="ghost" size="icon" className="rounded-full">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80">
            <div className="mt-6 mb-4 flex justify-center">
              <DayPhaseTimeline />
            </div>
            <div className="mt-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mnav-${l.label.toLowerCase().replace(/ /g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                      isActive ? "bg-[#0B132B] text-white" : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
